import { z } from "zod";

/**
 * Verbrauch aus Claude Code auf den Rechnern der Personen (tools/verbrauch). Nur Summen je 15 Minuten,
 * Modell und Projektordner, keine Inhalte. Signiert wie der taskFLOW-Kanal (HMAC-SHA256).
 */
export const USAGE_INTEGRATION_PATHS = {
  ingest: "POST /integrations/v1/usage/claude-code",
  report: "GET /integrations/v1/usage/report",
} as const;

const Label = z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, "nur Kleinbuchstaben, Ziffern und Bindestrich");
const Count = z.number().int().nonnegative().max(1e12);
const Slot = z.iso.datetime({ offset: true });

export const UsageIngestInput = z.strictObject({
  /** Rechner, von dem die Zahlen stammen, etwa `buero-pc`. */
  source: Label,
  /** Person, der der Verbrauch gehört; `owner` ist der Owner des Workspace. */
  person: Label,
  /** Zeitraum, den diese Meldung vollständig ersetzt. */
  from: Slot,
  to: Slot,
  slots: z
    .array(
      z.strictObject({
        slot: Slot,
        project: z.string().trim().min(1).max(120),
        model: z.string().trim().min(1).max(80),
        speed: z.enum(["standard", "fast"]),
        messages: Count,
        input: Count,
        output: Count,
        cacheRead: Count,
        cacheWrite5m: Count,
        cacheWrite1h: Count,
      }),
    )
    .max(20_000),
  commits: z
    .array(
      z.strictObject({
        slot: Slot,
        project: z.string().trim().min(1).max(120),
        commits: Count,
        linesAdded: Count,
        linesDeleted: Count,
      }),
    )
    .max(5_000),
});
export type UsageIngestInput = z.infer<typeof UsageIngestInput>;

/** Person im Bericht: gemessener Claude-Code-Verbrauch, beim Owner zusätzlich szyOS. */
export interface EfficiencyPerson {
  person: string;
  sources: string[];
  claudeCodeUsd: number;
  szyosUsd: number;
  /** Letzte Meldung eines Rechners dieser Person. */
  lastReportAt: string | null;
}

export interface EfficiencyWindow {
  from: string;
  to: string;
  /** `abo`: Woche des Wochenlimits; `kalender`: Kalenderwoche; `tage`: gleitender Zeitraum. */
  source: "abo" | "kalender" | "tage";
  szyos: { usd: number; runs: number; tasks: number; merges: number; lines: number };
  claudeCode: { usd: number; messages: number; commits: number; lines: number; unknownModels: string[] };
  comparison: {
    szyosUsdPer100Lines: number | null;
    claudeCodeUsdPer100Lines: number | null;
    factor: number | null;
    claudeCodeLinesForSzyosSpend: number | null;
    szyosUsdPerTask: number | null;
  };
  persons: EfficiencyPerson[];
}

export interface EfficiencyReport {
  generatedAt: string;
  week: EfficiencyWindow;
  last30Days: EfficiencyWindow;
  share: {
    accountUtilization: number | null;
    resetsAt: string | null;
    ownOfLimit: number | null;
    ownOfAccount: number | null;
    /** Anteil von szyOS und Claude Code am eigenen Verbrauch der Woche (0 bis 1). */
    szyosOfOwn: number | null;
    /** `richtwert`: veröffentlichter Wert für den Max-Plan 20x, bis eigene Messpunkte reichen. */
    method: "gemessen" | "geschätzt" | "richtwert" | "offen";
    intervals: number;
    usdPerFullLimit: number | null;
  };
  /** Bewährungsphase (docs/21): die vier messbaren Kriterien mit Ziel und Urteil. */
  trial: {
    from: string;
    to: string;
    daysLeft: number;
    criteria: { key: string; label: string; text: string; target: string; met: boolean | null }[];
  };
  /** Rechner, die Claude-Code-Verbrauch melden; leer, solange keiner eingerichtet ist. */
  collectors: { source: string; person: string; lastReportAt: string }[];
}
